import { ReactNode } from "react";

const normalizeBy = <T extends Record<string, any>>(array: T[], key: string | number) => {
  return array.reduce(
    (obj, item) => {
      obj[item[key]] = item;
      return obj;
    },
    {} as { [key: string | number]: T },
  );
};

const findValueByKeys = (obj: any, keys: string[]): string | undefined => {
  const dfs = (currentObj: any): string | undefined => {
    for (const [key, value] of Object.entries(currentObj)) {
      if (keys.includes(key) && typeof value === "string") {
        return value;
      }
      if (typeof value === "object" && value !== null) {
        const result = dfs(value);
        if (result !== undefined) {
          return result;
        }
      }
    }
    return undefined;
  };
  return dfs(obj);
};

const reduceObjectByKeys = (obj: any, keys: string[]): any => {
  const newObj: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (keys.includes(key)) {
      newObj[key] = value;
    }
    if (typeof value === "object" && value !== null) {
      newObj[key] = reduceObjectByKeys(value, keys);
    }
  }
  return newObj;
};

const keyValArrayToObject = (array: { key: string; value: any }[]) => {
  return array.reduce(
    (obj, item) => {
      obj[item.key] = item.value;
      return obj;
    },
    {} as { [key: string]: any },
  );
};

const renameProperties = <T,>(values: any, mapping: { [key: string]: string }): T => {
  return Object.keys(values).reduce((acc: T, key) => {
    const newKey = mapping[key] || key;
    (acc as any)[newKey] = values[key];
    return acc;
  }, {} as T);
};

const removePropertiesOnCondition = <T,>(values: any, keysAndConditions: { key: string; condition: (value: any) => boolean }[]): T => {
  keysAndConditions.forEach(({ key, condition }) => {
    if (values.hasOwnProperty(key) && condition(values[key])) {
      delete values[key];
    }
  });
  return values;
};

function base64ToBlob(base64: string, contentType: string = ""): Blob {
  const parts = base64.split(";");
  if (parts.length !== 2) {
    throw new Error("Invalid base64 string");
  }

  const match = parts[1].match(/base64,(.*)$/);
  if (!match || match.length !== 2) {
    throw new Error("Invalid base64 string");
  }

  const base64Data = match[1];
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
}

function objectToFormData(obj: any, form?: FormData, namespace?: string): FormData {
  const formData = form || new FormData();

  for (let property in obj) {
    if (!obj.hasOwnProperty(property) || obj[property] === undefined || obj[property] === null) continue;

    const formKey = namespace ? `${namespace}[${property}]` : property;

    if (typeof obj[property] === "object" && !(obj[property] instanceof File)) {
      objectToFormData(obj[property], formData, formKey);
    } else if (typeof obj[property] === "string" && obj[property].startsWith("data:image")) {
      const contentType = obj[property].split(";")[0].split(":")[1];
      const ext = contentType.split("/")[1];
      formData.append(formKey, base64ToBlob(obj[property], contentType), `filename.${ext}`);
    } else {
      formData.append(formKey, obj[property]);
    }
  }

  return formData;
}

const mergeUniqueOptions = (
  ...lists: Array<Array<{ label: ReactNode; value: number }> | undefined>
): { label: ReactNode; value: number }[] => {
  const map = new Map<number, { label: ReactNode; value: number }>();

  lists.forEach((list) => list?.forEach((item) => map.set(item.value, item)));

  return Array.from(map.values());
};
type PathTree = {
  [key: string]: {
    path: (string | number)[];
    default?: any;
  } | PathTree;
};

type ResolvedTree<T extends PathTree> = {
  [K in keyof T]: any;
};

const isPathEntry = (value: unknown): value is { path: (string | number)[]; default?: any } =>
  typeof value === "object" &&
  value !== null &&
  "path" in value &&
  Array.isArray((value as any).path);

const getValues = <T extends PathTree>(
  obj: Record<string, any>,
  tree: T
): ResolvedTree<T> => {
  return Object.fromEntries(
    Object.entries(tree).map(([key, value]) => {
      if (isPathEntry(value)) {
        const resolved = value.path.reduce((acc, k) => acc?.[k], obj);
        return [key, resolved ?? value.default];
      }
      return [key, getValues(obj, value as PathTree)];
    })
  ) as ResolvedTree<T>;
};

const getValue = (obj: Record<string, any>, path: (string | number)[], defaultValue?: any): any =>
  path.reduce((acc, key) => acc?.[key], obj) ?? defaultValue;


function explode<T extends Record<string, any>>(
  data: T,
  fallback: Partial<T>
): T {
  const result = { ...data };
  (Object.keys(fallback) as (keyof T)[]).forEach((key) => {
    const value = data[key];
    if (value === undefined || value === null || value === "") {
      result[key] = fallback[key] as T[keyof T];
    }
  });

  return result;
}

type TransformerConfig = {
  label: ReactNode;
  value?: (val: any, extras?: Record<string, any>) => any;
};

function transformRecord<T extends Record<string, any>>(
  data: T,
  transformer: Partial<Record<keyof T, TransformerConfig>>,
  extras?: Record<string, any>
): { key: string; label: ReactNode; display: any }[] {
  return (Object.keys(transformer) as (keyof T)[])
    .filter((key) => data && key in data)
    .map((key) => {
      const config = transformer[key]!;
      return {
        key: key as string,
        label: config.label,
        display: config.value ? config.value(data[key], extras) : data[key],
      };
    });
}


function getStoredData(localStorageKey?: string, itemKey?: string) {
  if (!localStorageKey || !itemKey) return;
  try {
    const data = localStorage.getItem(localStorageKey);
    const dataObject = JSON.parse(data ?? "");
    return dataObject?.[itemKey];
  } catch (err) {}
}

function expireStoredSession(...localStorageKeys: (string | undefined)[]) {
  localStorageKeys.forEach((key) => {
    if (!key) return;
    try {
      const data = JSON.parse(localStorage.getItem(key) ?? "");
      if (!data?._session) return;
      localStorage.setItem(key, JSON.stringify({ ...data, _session: { ...data._session, maxTime: 0 } }));
    } catch (err) {}
  });
}

export {
  transformRecord,
  mergeUniqueOptions,
  base64ToBlob,
  findValueByKeys,
  keyValArrayToObject,
  normalizeBy,
  objectToFormData,
  reduceObjectByKeys,
  removePropertiesOnCondition,
  renameProperties,
  getValue,
  explode,
  getValues,
  getStoredData,
  expireStoredSession,
};

export type {TransformerConfig};